import { FC, PropsWithChildren, useEffect } from 'react';
import { DPXWorkflowLandingPage } from '@devxprotect/plugin-devxprotect-workflow';
import { Route, useLocation } from 'react-router';
import { apiDocsPlugin, ApiExplorerPage } from '@backstage/plugin-api-docs';
import {
  CatalogEntityPage,
  CatalogIndexPage,
  catalogPlugin,
} from '@backstage/plugin-catalog';
import { catalogImportPlugin } from '@backstage/plugin-catalog-import';
import {
  ScaffolderFieldExtensions,
  ScaffolderPage,
  scaffolderPlugin,
} from '@backstage/plugin-scaffolder';
import { orgPlugin } from '@backstage/plugin-org';
import { SearchPage } from '@backstage/plugin-search';
import {
  TechDocsIndexPage,
  techdocsPlugin,
  TechDocsReaderPage,
  TechDocsReaderPageContent,
} from '@backstage/plugin-techdocs';
import { TechDocsAddons } from '@backstage/plugin-techdocs-react';
import { ReportIssue } from '@backstage/plugin-techdocs-module-addons-contrib';
import { TechDocsWrapper } from './custom/components/techdocs/TechDocsWrapper';
import { UserSettingsPage } from '@backstage/plugin-user-settings';
import { NotificationsPage } from '@backstage/plugin-notifications';
import { apis } from './apis';
import { entityPage } from './components/catalog/EntityPage';
import { Root } from './components/Root';

import {
  AlertDisplay,
  Content,
  Header,
  OAuthRequestDialog,
  Page,
} from '@backstage/core-components';
import { SignalsDisplay } from '@backstage/plugin-signals';
import { createApp } from '@backstage/app-defaults';
import { FlatRoutes } from '@backstage/core-app-api';
import { CatalogGraphPage } from '@backstage/plugin-catalog-graph';
import {
  ActiveDirectoryGroupPickerExtension,
  ActiveDirectoryGroupPickerMultiExtension,
  ApiDropdownComponentExtension,
  ApiMultiSelectComponentExtension,
  AzureActiveDirectoryGroupPickerExtension,
  customComponents,
  DBaaSAppPickerExtension,
  ExistingEntityPickerExtension,
  FormContextFieldValueExtension,
  GroupEntityPickerExtension,
  IconCardPickerExtension,
  ApplicationPickerExtension,
  ArtifactoryPermissionPickerExtension,
  ArtifactoryProjectPickerExtension,
  OCPConfigFilePickerExtension,
  OwnedITRCAppPickerExtension,
  OwnedRepoUrlPickerExtension,
} from './custom/app/components';
import { customIcons } from './custom/app/icons';
import { HomepageCompositionRoot } from '@backstage/plugin-home';
import { DevHubHomePage } from './custom/components/home/DevHubHomePage';
import { Settings } from './custom/components/settings/Settings';
import {
  darkModeTheme,
  DevHubCssBaseline,
  DevHubDynamicIcon,
  DevHubThemeProvider,
  lightModeTheme,
} from '@devxdiscover/devhub-ui';
import { authProviders } from './custom/components/settings/AuthProviders';
import { OnboardingPage } from './custom/components/onboarding/Onboarding';
import { FusionProvider } from './custom/contexts/FusionContext';
import { UserProvider } from './custom/contexts/UserContext';
import { FunctionalAreaProvider } from './custom/contexts/FunctionalAreaContext';
import AppCreation from './custom/components/onboarding/AppCreation';
import { MyApps } from './custom/components/apps/MyApps';
import { ManageDataLeadsContainer } from './custom/components/apps/DataLeadManagement/components';
import { PluginManager, PluginType } from './components/plugin/PluginManager';
import { UsersCloudAccounts } from './custom/components/insights/cloudAccounts/UsersCloudAccounts';
import {
  EntityPluginDevxprotectExperienceTeams,
  PluginDevxprotectExperienceInfo,
} from '@devxprotect/plugin-devxprotect-experience';
import './custom/components/overrides/overrides.css';
import 'primereact/resources/themes/lara-light-indigo/theme.css';
import 'primereact/resources/primereact.min.css';
import DeviceListPage from './custom/components/DeviceListPage';
import AgentChatPage from './custom/components/agent/AgentChatPage';
import { Connections } from './custom/components/insights/connections/Connections';
import { PrimeReactProvider } from 'primereact/api';
import { CatalogSearch } from './custom/components/catalog/CatalogSearch';
import { DevhubSearchPage } from './components/search/SearchPage';
import {
  InterceptRoute,
  routeInterceptor,
  templateInterceptor,
} from './custom/app/router/InterceptRoute';
import { ConfigHelper, Feature } from '@devxdiscover/devhub-common';
import { Navigate, useNavigate, useParams } from 'react-router-dom';
import { ActionCenter } from './custom/components/home/ActionCenter';
import { ApiPortalOrg } from './custom/components/portals/ApiPortalOrg';
import { ApiPortals } from './custom/components/portals/ApiPortals';
import ApiPortalCatalog from './custom/components/portals/ApiPortalCatalog';
import { ApiPortalProvider } from './custom/contexts/ApiPortalContext';
import ApiPortalCategory from './custom/components/portals/ApiPortalCategory';
import ApiPortalDocs from './custom/components/portals/ApiPortalDocs';
import ApiPortalDocPage from './custom/components/portals/ApiPortalDocPage';
import ApiSpecDocs from './custom/components/portals/ApiSpecDocs';
import AppBulkEdit from './custom/components/apps/AppBulkEdit/AppBulkEdit';
import { SelectedAppProvider } from './custom/contexts/SelectedAppContext';
import { specCategoryMap } from './custom/components/portals/specCategoryMap';
import { apiPortalRedirects } from './custom/components/portals/apiPortalRedirects';
import { ApiPortalRedirect } from './custom/components/portals/ApiPortalRedirect';
import { ApiPortalDataFeedManagement } from './custom/components/portals/ApiPortalDataFeedManagement';
import { ConfigFeatureCheck } from './custom/util/ConfigFeatureCheck';
import useGoogleTagManager from './custom/util/UseGoogleTagManager';
import { DocumentationHome } from './custom/components/catalog/DocumentationHome';
import { QetaPage } from '@drodil/backstage-plugin-qeta';
import NewMarketplace from './custom/components/NewMarketplace';
import { AnnouncementsPage } from '@backstage-community/plugin-announcements';
import { RefreshStatusPage } from './custom/components/catalog/RefreshStatusPage.tsx';
import { CGAHierarchyReportPage } from './custom/components/cga/CGAHierarchyReportPage';
import { PolicyAccessCheck } from './custom/util/PolicyAccessCheck';
import DetPortal from './custom/components/DetPortal';
import { FlowHubPage } from './custom/components/flowhub/FlowHubPage';

const app = createApp({
  apis,
  themes: [
    {
      id: 'devhub-theme',
      title: 'DevHub Light',
      variant: 'light',
      icon: <DevHubDynamicIcon iconName="Light" />,
      Provider: ({ children }) => (
        <DevHubThemeProvider theme={lightModeTheme}>
          <DevHubCssBaseline>{children}</DevHubCssBaseline>
        </DevHubThemeProvider>
      ),
    },
    {
      id: 'devhub-dark',
      title: 'DevHub Dark',
      variant: 'light',
      icon: <DevHubDynamicIcon iconName="NightsStay" />,
      Provider: ({ children }) => (
        <DevHubThemeProvider theme={darkModeTheme}>
          <DevHubCssBaseline>{children}</DevHubCssBaseline>
        </DevHubThemeProvider>
      ),
    },
  ],
  icons: customIcons,
  components: customComponents,
  bindRoutes({ bind }) {
    bind(catalogPlugin.externalRoutes, {
      createComponent: scaffolderPlugin.routes.root,
      viewTechDoc: techdocsPlugin.routes.docRoot,
    });
    bind(apiDocsPlugin.externalRoutes, {
      registerApi: catalogImportPlugin.routes.importPage,
    });
    bind(scaffolderPlugin.externalRoutes, {
      registerComponent: catalogImportPlugin.routes.importPage,
    });
    bind(orgPlugin.externalRoutes, {
      catalogIndex: catalogPlugin.routes.catalogIndex,
    });
  },
});

interface RedirectToExternalProps {
  templateID?: string;
  redirectLink?: string;
}

const RedirectToExternal: FC<RedirectToExternalProps> = ({
  templateID,
  redirectLink,
}) => {
  const navigate = useNavigate();
  useEffect(() => {
    const redirectToExternal = async () => {
      if (templateID && redirectLink) {
        window.open(redirectLink!, '_blank');
        navigate('/marketplace'); // Stay on the marketplace page
      }
    };

    redirectToExternal();
  }, [templateID, redirectLink, navigate]);

  return null;
};

const AppProvider = app.getProvider();
const AppRouter = app.getRouter();

// ⚠️ Workaround for `/sdl-team/:teamId` route. DO NOT COPY. ⚠️
//
// We need to get the `teamId` from React Router `params` and pass it to the EntityPluginDevxprotectExperienceTeams
// component so that it can fetch the correct team when the teamId changes. The `key` prop is used to force the
// component to re-render when the teamId changes.
//
// Ideally, the DevX Protect team would update their plugin to include the `teamId` as a `key` for their component
// internally, but this functionality was working before our changes to Root.tsx which forces anchor tags to use React
// Router. DevX Protect should also use `useParams` instead of parsing the URL directly (as best practice).
const EntityPluginDevxprotectExperienceTeamsWrapper = () => {
  const { teamId } = useParams<{ teamId: string }>();
  return <EntityPluginDevxprotectExperienceTeams key={teamId} />;
};

const KongRedirectToApiPortal: FC = () => {
  const { categoryName, docName } = useParams<{
    orgName: string;
    categoryName: string;
    docName: string;
  }>();
  const location = useLocation();

  const docType = location.pathname.includes('/event/')
    ? 'events'
    : 'scenarios';
  const sanitizedDocName = docName?.replace(/^event-/, '');
  const newUrl = `/api-portals/connected-living/${categoryName}/${docType}/${sanitizedDocName}`;
  return <Navigate to={newUrl} replace />;
};

const KongRedirectToApiPortalSpecs: FC = () => {
  const { specName, categoryName } = useParams<{
    specName?: string;
    categoryName?: string;
  }>();
  const location = useLocation();

  let newUrl = '';
  if (specName) {
    const mappedCategoryName = specCategoryMap[specName];
    if (location.pathname.includes('/documentation/')) {
      newUrl = `/api-portals/connected-living/${mappedCategoryName}/${specName}`;
    }
  }

  if (location.pathname.includes('/default/categories')) {
    if (location.pathname.includes('/catalog')) {
      newUrl = `/api-portals/connected-living/${categoryName}/specs`;
    } else {
      newUrl = `/api-portals/connected-living/${categoryName}`;
    }
  }
  return <Navigate to={newUrl} replace />;
};

const RedirectToCatalog: FC = () => {
  const { id, '*': remainingPath } = useParams<{ id: string; '*': string }>();

  const redirectPath = `/catalog/default/system/${id}/${remainingPath || ''}`;

  return <Navigate to={redirectPath} replace />;
};

const routes = (
  <>
    {/* --- API PORTAL ROUTES --- */}

    {Object.entries(apiPortalRedirects).map(([from, to]) => (
      <Route key={from} path={from} element={<ApiPortalRedirect to={to} />} />
    ))}
    <Route path="/default" element={<Navigate to="/api-portals" />} />
    <Route
      path="/default/apiman"
      element={<Navigate to="/api-portals/connected-living" />}
    />
    <Route
      path="/default/categories/:categoryName/:docType/:docName"
      element={<KongRedirectToApiPortal />}
    />
    <Route
      path="/default/categories/:categoryName/catalog"
      element={<KongRedirectToApiPortalSpecs />}
    />
    <Route
      path="/default/categories/:categoryName"
      element={<KongRedirectToApiPortalSpecs />}
    />
    <Route
      path="/default/documentation/:specName"
      element={<KongRedirectToApiPortalSpecs />}
    />
    <Route path="/api-portals" element={<ApiPortals />} />
    <Route
      path="/api-portals/:orgName"
      element={
        <ApiPortalProvider>
          <ApiPortalOrg isMainPage />
        </ApiPortalProvider>
      }
    />
    <Route
      path="/api-portals/:orgName/data-feed-management"
      element={
        <ApiPortalProvider>
          <ApiPortalDataFeedManagement />
        </ApiPortalProvider>
      }
    />
    <Route
      path="/api-portals/:orgName/data-feed-management/:docName"
      element={
        <ApiPortalProvider>
          <ApiPortalDocPage isDataFeed />
        </ApiPortalProvider>
      }
    />
    <Route
      path="/api-portals/:orgName/catalog"
      element={
        <ApiPortalProvider>
          <ApiPortalCatalog />
        </ApiPortalProvider>
      }
    />
    <Route
      path="/api-portals/:orgName/:categoryName"
      element={
        <ApiPortalProvider>
          <ApiPortalCategory />
        </ApiPortalProvider>
      }
    />
    <Route
      path="/api-portals/:orgName/:categoryName/:docType"
      element={
        <ApiPortalProvider>
          <ApiPortalDocs />
        </ApiPortalProvider>
      }
    />
    <Route
      path="/api-portals/:orgName/:categoryName/:docType/:docName"
      element={
        <ApiPortalProvider>
          <ApiPortalDocPage />
        </ApiPortalProvider>
      }
    />
    <Route
      path="/api-portals/:orgName/:categoryName/specs"
      element={
        <ApiPortalProvider>
          <ApiPortalCatalog />
        </ApiPortalProvider>
      }
    />
    <Route path="/dpx-workflow" element={<DPXWorkflowLandingPage />} />
    <Route
      path="/api-portals/:orgName/:categoryName/specs/:specName"
      element={
        <ApiPortalProvider>
          <ApiSpecDocs />
        </ApiPortalProvider>
      }
    />

    {/* --- ACTION CENTER ROUTES --- */}
    <Route
      path="/action-center"
      element={
        <ConfigFeatureCheck feature={Feature.ACTION_CENTER}>
          <ActionCenter />
        </ConfigFeatureCheck>
      }
    />

    {/* --- APPLICATION ROUTES --- */}
    <Route
      path="/app/id/:id/*"
      element={
        <ConfigFeatureCheck feature={Feature.APPLICATIONS}>
          <RedirectToCatalog />
        </ConfigFeatureCheck>
      }
    />
    <Route
      path="/application"
      element={
        <ConfigFeatureCheck feature={Feature.APPLICATIONS}>
          <CatalogIndexPage />
        </ConfigFeatureCheck>
      }
    />
    <Route
      path="/my-apps"
      element={
        <ConfigFeatureCheck feature={Feature.APPLICATIONS}>
          <SelectedAppProvider>
            <FunctionalAreaProvider>
              <MyApps />
            </FunctionalAreaProvider>
          </SelectedAppProvider>
        </ConfigFeatureCheck>
      }
    />
    <Route
      path="/my-apps/bulk-edit"
      element={
        <ConfigFeatureCheck feature={Feature.APP_BULK_EDIT}>
          <SelectedAppProvider>
            <AppBulkEdit />
          </SelectedAppProvider>
        </ConfigFeatureCheck>
      }
    />
    <Route
      path="/my-apps/manage-data-leads"
      element={
        <ConfigFeatureCheck feature={Feature.DBO_MANAGEMENT}>
          <FunctionalAreaProvider>
            <PrimeReactProvider>
              <ManageDataLeadsContainer />
            </PrimeReactProvider>
          </FunctionalAreaProvider>
        </ConfigFeatureCheck>
      }
    />

    {/* --- CATALOG ROUTES --- */}
    <Route
      path="/catalog-graph"
      element={
        <ConfigFeatureCheck feature={Feature.CATALOG}>
          <CatalogGraphPage />
        </ConfigFeatureCheck>
      }
    />
    <Route
      path="/catalog-search"
      element={
        <ConfigFeatureCheck feature={Feature.CATALOG}>
          <PrimeReactProvider>
            <CatalogSearch key="catalog-search" />
          </PrimeReactProvider>
        </ConfigFeatureCheck>
      }
    />
    <Route
      path="/catalog/:namespace/:kind/:name"
      element={
        <ConfigFeatureCheck feature={Feature.CATALOG}>
          <InterceptRoute
            canIntercept={params =>
              ['component', 'api'].includes(params.kind?.toLowerCase() ?? '') &&
              !/^[a-f0-9]{40}$/.test(params.name ?? '')
            }
            intercept={routeInterceptor('catalog')}
          >
            <CatalogEntityPage />
          </InterceptRoute>
        </ConfigFeatureCheck>
      }
    >
      {entityPage}
    </Route>

    {/* --- CLOUD ACCOUNT ROUTES --- */}
    <Route
      path="/cloud-accounts"
      element={
        <ConfigFeatureCheck feature={Feature.CLOUD_ACCOUNTS}>
          <Page themeId="home">
            <Header
              title="Cloud Accounts"
              subtitle="Cloud Accounts associated to you in OneCloud"
            />
            <Content>
              <UsersCloudAccounts />
            </Content>
          </Page>
        </ConfigFeatureCheck>
      }
    />

    {/* --- COMMUNITY ROUTES --- */}
    <Route
      path="/community/:namespace/:kind/:name/*"
      element={
        <ConfigFeatureCheck feature={Feature.COMMUNITY}>
          <InterceptRoute
            canIntercept={params =>
              ['component', 'api'].includes(params.kind?.toLowerCase() ?? '') &&
              !/^[a-f0-9]{40}$/.test(params.name ?? '')
            }
            intercept={routeInterceptor('community')}
          >
            <TechDocsWrapper>
              <TechDocsReaderPage />
            </TechDocsWrapper>
          </InterceptRoute>
        </ConfigFeatureCheck>
      }
    >
      <TechDocsWrapper>
        <Page themeId="catalog">
          <Header
            title="DevHub Community Updates"
            subtitle="Updates about all things DevHub"
          />
          <TechDocsReaderPageContent withSearch={false} />
        </Page>
      </TechDocsWrapper>
      <TechDocsAddons>
        <ReportIssue />
      </TechDocsAddons>
    </Route>

    {/* --- FLOWHUB ROUTES --- */}
    <Route
      path="/flowhub/*"
      element={
        <ConfigFeatureCheck feature={Feature.FLOWHUB}>
          <FlowHubPage />
        </ConfigFeatureCheck>
      }
    />

    {/* --- CONNECTION ROUTES --- */}
    <Route
      path="/connections/*"
      element={
        <ConfigFeatureCheck feature={Feature.CONNECTIONS}>
          <Connections />
        </ConfigFeatureCheck>
      }
    />
    <Route
      path="/device-list"
      element={
        <ConfigFeatureCheck feature={Feature.CONNECTIONS}>
          <DeviceListPage />
        </ConfigFeatureCheck>
      }
    />

    {/* --- DOCUMENTATION ROUTES --- */}
    <Route
      path="/api-docs"
      element={
        <ConfigFeatureCheck feature={Feature.DOCUMENTATION}>
          <ApiExplorerPage />
        </ConfigFeatureCheck>
      }
    />
    <Route
      path="/docs"
      element={
        <ConfigFeatureCheck feature={Feature.DOCUMENTATION}>
          <TechDocsIndexPage />
        </ConfigFeatureCheck>
      }
    />
    <Route
      path="/documentation"
      element={
        <ConfigFeatureCheck feature={Feature.DOCUMENTATION}>
          <DocumentationHome />
        </ConfigFeatureCheck>
      }
    />
    <Route
      path="/docs-search"
      element={
        <ConfigFeatureCheck feature={Feature.DOCUMENTATION}>
          <PrimeReactProvider>
            <CatalogSearch key="docs-search" />
          </PrimeReactProvider>
        </ConfigFeatureCheck>
      }
    />
    <Route
      path="/docs/:namespace/:kind/:name/*"
      element={
        <ConfigFeatureCheck feature={Feature.DOCUMENTATION}>
          <InterceptRoute
            canIntercept={params =>
              ['component', 'api'].includes(params.kind?.toLowerCase() ?? '') &&
              !/^[a-f0-9]{40}$/.test(params.name ?? '')
            }
            intercept={routeInterceptor('docs')}
          >
            <TechDocsWrapper>
              <TechDocsReaderPage />
            </TechDocsWrapper>
          </InterceptRoute>
        </ConfigFeatureCheck>
      }
    >
      <TechDocsAddons>
        <ReportIssue />
      </TechDocsAddons>
    </Route>
    <Route
      path="/cheat-sheet/:namespace/:kind/:name/*"
      element={
        <ConfigFeatureCheck feature={Feature.DOCUMENTATION}>
          <InterceptRoute
            canIntercept={params =>
              ['component', 'api'].includes(params.kind?.toLowerCase() ?? '') &&
              !/^[a-f0-9]{40}$/.test(params.name ?? '')
            }
            intercept={routeInterceptor('cheat-sheet')}
          >
            <TechDocsWrapper>
              <TechDocsReaderPage />
            </TechDocsWrapper>
          </InterceptRoute>
        </ConfigFeatureCheck>
      }
    >
      <TechDocsWrapper>
        <Page themeId="documentation">
          <Header
            title="Developer Cheat Sheet"
            subtitle="Services available throughout Comcast"
          />
          <TechDocsReaderPageContent withSearch={false} />
        </Page>
      </TechDocsWrapper>
      <TechDocsAddons>
        <ReportIssue />
      </TechDocsAddons>
    </Route>

    {/* --- MARKETPLACE ROUTES --- */}
    <Route
      path="/marketplace"
      element={
        <ConfigFeatureCheck feature={Feature.MARKETPLACE}>
          <ScaffolderPage
            headerOptions={{
              title: 'Marketplace',
              subtitle:
                'A hub at the center for discovering plugins, services, and other offerings.',
              pageTitleOverride: 'Marketplace',
            }}
            groups={[
              {
                title: 'Onboarding',
                filter: entity =>
                  entity?.metadata?.tags?.includes('onboarding') ?? false,
              },
              {
                title: 'External',
                filter: entity =>
                  entity?.metadata?.tags?.includes('external') ?? false,
              },
            ]}
          />
        </ConfigFeatureCheck>
      }
    >
      <ScaffolderFieldExtensions>
        <FormContextFieldValueExtension />
        <ExistingEntityPickerExtension />
        <GroupEntityPickerExtension />
        <DBaaSAppPickerExtension />
        <OwnedITRCAppPickerExtension />
        <OwnedRepoUrlPickerExtension />
        <ApiDropdownComponentExtension />
        <ApiMultiSelectComponentExtension />
        <ActiveDirectoryGroupPickerExtension />
        <OCPConfigFilePickerExtension />
        <AzureActiveDirectoryGroupPickerExtension />
        <ActiveDirectoryGroupPickerMultiExtension />
        <IconCardPickerExtension />
        <ApplicationPickerExtension />
        <ArtifactoryPermissionPickerExtension />
        <ArtifactoryProjectPickerExtension />
      </ScaffolderFieldExtensions>
    </Route>

    <Route
      path="/newmarketplace"
      element={
        <ConfigFeatureCheck feature={Feature.NEW_MARKETPLACE}>
          <NewMarketplace />
        </ConfigFeatureCheck>
      }
    />

    <Route
      path="/det-portal"
      element={
        <PolicyAccessCheck policy="AccessDETAdminPagePolicy" hideWhileLoading>
          <DetPortal />
        </PolicyAccessCheck>
      }
    />

    <Route
      path="/marketplace/templates/default/:name"
      element={
        <ConfigFeatureCheck feature={Feature.MARKETPLACE}>
          <InterceptRoute
            canIntercept={() => true}
            intercept={templateInterceptor('marketplace')}
          />
        </ConfigFeatureCheck>
      }
    />

    {/* --- ONBOARDING ROUTES --- */}
    <Route
      path="/onboarding"
      element={
        <ConfigFeatureCheck feature={Feature.ONBOARDING}>
          <OnboardingPage />
        </ConfigFeatureCheck>
      }
    />
    <Route
      path="/onboarding/create"
      element={
        <ConfigFeatureCheck feature={Feature.ONBOARDING}>
          <AppCreation />
        </ConfigFeatureCheck>
      }
    />

    {/* --- OTHER ROUTES --- */}
    <Route
      path="/solutions"
      element={
        <ConfigFeatureCheck feature={Feature.SOLUTIONS}>
          <QetaPage
            title="Solutions"
            subtitle="Collaborate with others, solve problems, and share knowledge"
          />
        </ConfigFeatureCheck>
      }
    />
    <Route
      path="/cga-hierarchy-report"
      element={
        <ConfigFeatureCheck feature={Feature.CGA_HIERARCHY_REPORT}>
          <PolicyAccessCheck
            policy="AccessCGAHierarchyReportPolicy"
            hideWhileLoading
          >
            <CGAHierarchyReportPage />
          </PolicyAccessCheck>
        </ConfigFeatureCheck>
      }
    />
    <Route path="/announcements" element={<AnnouncementsPage />} />
    <Route path="/settings" element={<Settings />} />
    <Route path="/" element={<HomepageCompositionRoot />}>
      <DevHubHomePage />
    </Route>
    <Route path="/search" element={<SearchPage />}>
      {DevhubSearchPage}
    </Route>
    <Route
      path="/settings"
      element={<UserSettingsPage providerSettings={authProviders} />}
    />
    <Route
      path="/notifications"
      element={
        <ConfigFeatureCheck feature={Feature.NOTIFICATIONS}>
          <NotificationsPage />
        </ConfigFeatureCheck>
      }
    />
    <Route path="/refresh-status/:runId" element={<RefreshStatusPage />} />
    <Route
      path="/agent"
      element={
        <ConfigFeatureCheck feature={Feature.NOVA}>
          <AgentChatPage />
        </ConfigFeatureCheck>
      }
    />
    <Route
      path="/sdl-team/:teamId"
      element={
        <PluginManager
          type={PluginType.FULL_PAGE}
          title="My SDL Teams"
          modalContent={PluginDevxprotectExperienceInfo}
        >
          <EntityPluginDevxprotectExperienceTeamsWrapper />
        </PluginManager>
      }
    />
  </>
);

const DynamicRouteDirects = ({ children }: PropsWithChildren<{}>) => {
  const location = useLocation();
  useGoogleTagManager();
  useEffect(() => {
    const routeTitleMap: { [key: string]: string } = {
      '/': 'DevHub',
    };

    document.title = routeTitleMap[location.pathname] || 'DevHub';
  }, [location.pathname]);

  const externalMarketplaceLinks =
    ConfigHelper?.instance?.externalMarketplaceLinks;
  const templateRouteRedirects = ConfigHelper?.instance?.templateRedirectLinks;
  const dynamicRoutes = templateRouteRedirects?.map(redirectLink => {
    const [from, to] = redirectLink.split('->');
    return (
      // split redirectLink separated by -> and split into from and to
      <Route key={from} path={from} element={<Navigate to={to} replace />} />
    );
  });

  const marketplaceRoutes = externalMarketplaceLinks.map(redirectLink => {
    const [from, to] = redirectLink.split('->');
    return (
      // split redirectLink separated by -> and split into from and to
      <Route
        key={from}
        path={`/marketplace/templates/v2/default/${from}`}
        element={<RedirectToExternal templateID={from} redirectLink={to} />}
      />
    );
  });
  return (
    <>
      {ConfigHelper.instance.isFeatureEnabled(
        Feature.DEVHUB_PUBLIC_HOMEPAGE,
      ) ? (
        <FlatRoutes>
          {dynamicRoutes}
          {marketplaceRoutes}
          {children}
        </FlatRoutes>
      ) : (
        <UserProvider>
          <FlatRoutes>
            {dynamicRoutes}
            {marketplaceRoutes}
            {children}
          </FlatRoutes>
        </UserProvider>
      )}
    </>
  );
};

/**
 * The main application container.
 * @constructor
 */
const App = () => (
  <AppProvider>
    <AlertDisplay />
    <OAuthRequestDialog />
    <SignalsDisplay />
    <AppRouter>
      <Root>
        <FusionProvider>
          <DynamicRouteDirects>{routes}</DynamicRouteDirects>
        </FusionProvider>
      </Root>
    </AppRouter>
  </AppProvider>
);

export default App;
